window.themeLog = window.themeLog || function () {};
/**
 * PRODUCT CARD STOCK SYNC
 *
 * Syncs product card stock status with real-time inventory data
 * Fixes caching issue where collection pages show outdated stock status
 *
 * Usage: Automatically runs on collection/homepage/search pages
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    // Selectors
    productCardSelector: '.product-card, article[itemtype="https://schema.org/Product"]',
    stockIndicatorSelector: '.product-card-stock',
    badgeSelector: '.product-badge-sold-out, .product-card__badge--sold-out',
    availabilityMetaSelector: 'link[itemprop="availability"]',
    quickAddSelector: '.product-card-quick-add, .product-card__quick-add, .product-card-actions',

    // Stock status classes
    inStockClass: 'in-stock',
    outOfStockClass: 'out-of-stock',

    // Debounce delay (ms) to avoid hammering the API
    debounceDelay: 100,

    // Batch size for API requests
    batchSize: 10,
    batchDelay: 200, // ms between batches
  };

  /**
   * Extract product handle from card element
   */
  function getProductHandle(card) {
    // Try to get from link href
    const link = card.querySelector('a[href*="/products/"]');
    if (link) {
      const match = link.href.match(/\/products\/([^?#/]+)/);
      if (match) return match[1];
    }

    // Try to get from quick add button
    const quickAddBtn = card.querySelector('[data-product-handle]');
    if (quickAddBtn) {
      return quickAddBtn.dataset.productHandle;
    }

    return null;
  }

  /**
   * Fetch real-time product data from Shopify Product API
   */
  async function fetchProductData(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      console.warn(`Failed to fetch product data for ${handle}:`, error);
      return null;
    }
  }

  /**
   * Check if product has any available variants based on inventory quantity
   * This checks actual stock levels, not the 'available' flag which can be
   * affected by "Continue selling when out of stock" setting
   */
  function isProductAvailable(productData) {
    if (!productData || !productData.variants) return false;

    // Check if ANY variant has inventory > 0
    // This ignores the 'available' flag and checks actual stock
    const hasStock = productData.variants.some(variant => {
      // If inventory_quantity is null or undefined, fall back to available flag
      if (variant.inventory_quantity === null || variant.inventory_quantity === undefined) {
        return variant.available;
      }
      // Check actual inventory quantity
      return variant.inventory_quantity > 0;
    });

    return hasStock;
  }

  /**
   * Update stock indicator element
   */
  function updateStockIndicator(card, isAvailable, inStockText, outOfStockText) {
    const stockIndicator = card.querySelector(CONFIG.stockIndicatorSelector);
    if (!stockIndicator) return;

    // Update classes
    stockIndicator.classList.remove(CONFIG.inStockClass, CONFIG.outOfStockClass);
    stockIndicator.classList.add(isAvailable ? CONFIG.inStockClass : CONFIG.outOfStockClass);

    // Update text
    stockIndicator.textContent = isAvailable ? inStockText : outOfStockText;
  }

  /**
   * Update sold out badge
   */
  function updateSoldOutBadge(card, isAvailable) {
    const badge = card.querySelector(CONFIG.badgeSelector);
    if (!badge) return;

    if (isAvailable) {
      // Product is available, remove badge
      badge.style.display = 'none';
    } else {
      // Product is sold out, show badge
      badge.style.display = '';
    }
  }

  /**
   * Update schema.org availability meta
   */
  function updateAvailabilityMeta(card, isAvailable) {
    const meta = card.querySelector(CONFIG.availabilityMetaSelector);
    if (!meta) return;

    meta.setAttribute(
      'href',
      isAvailable ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock'
    );
  }

  /**
   * Update quick add button visibility
   */
  function updateQuickAddButton(card, isAvailable) {
    const quickAdd = card.querySelector(CONFIG.quickAddSelector);
    if (!quickAdd) return;

    if (isAvailable) {
      quickAdd.style.display = '';
    } else {
      quickAdd.style.display = 'none';
    }
  }

  /**
   * Update a single product card with real-time stock data
   */
  async function updateProductCard(card, inStockText, outOfStockText) {
    const handle = getProductHandle(card);
    if (!handle) {
      console.warn('Could not extract product handle from card:', card);
      return;
    }

    // Fetch real-time product data
    const productData = await fetchProductData(handle);
    if (!productData) return;

    // Check availability based on inventory quantity
    const isAvailable = isProductAvailable(productData);

    // Get current displayed availability
    const stockIndicator = card.querySelector(CONFIG.stockIndicatorSelector);
    const currentlyShowsInStock = stockIndicator && stockIndicator.classList.contains(CONFIG.inStockClass);

    // Log inventory details for debugging
    const inventoryInfo = productData.variants.map(v => ({
      title: v.title,
      inventory_quantity: v.inventory_quantity,
      available: v.available
    }));
    themeLog(`Stock check for ${handle}:`, {
      isAvailable,
      currentlyShowsInStock,
      variants: inventoryInfo
    });

    // Only update if status has changed (avoid unnecessary DOM updates)
    if (currentlyShowsInStock !== isAvailable) {
      themeLog(`✓ Updating stock status for ${handle}: ${isAvailable ? 'In Stock' : 'Out of Stock'}`);

      // Update all stock-related elements
      updateStockIndicator(card, isAvailable, inStockText, outOfStockText);
      updateSoldOutBadge(card, isAvailable);
      updateAvailabilityMeta(card, isAvailable);
      updateQuickAddButton(card, isAvailable);

      // Dispatch custom event for other scripts to listen to
      card.dispatchEvent(new CustomEvent('stockStatusUpdated', {
        detail: { handle, isAvailable, productData }
      }));
    }
  }

  /**
   * Process product cards in batches to avoid overwhelming the API
   */
  async function processCardsInBatches(cards, inStockText, outOfStockText) {
    const batches = [];

    // Split cards into batches
    for (let i = 0; i < cards.length; i += CONFIG.batchSize) {
      batches.push(Array.from(cards).slice(i, i + CONFIG.batchSize));
    }

    // Process each batch with delay
    for (let i = 0; i < batches.length; i++) {
      const batch = batches[i];

      // Process all cards in this batch concurrently
      await Promise.all(
        batch.map(card => updateProductCard(card, inStockText, outOfStockText))
      );

      // Wait before processing next batch (except for last batch)
      if (i < batches.length - 1) {
        await new Promise(resolve => setTimeout(resolve, CONFIG.batchDelay));
      }
    }
  }

  /**
   * Get stock status text from section settings or use defaults
   */
  function getStockStatusText() {
    // Try to get from section settings (check multiple possible section IDs)
    const sectionSelectors = [
      '[data-section-type="collection"]',
      '[data-section-type="homepage-products"]',
      '[data-section-type="search"]',
      '.main-collection',
      '.homepage-products-section',
      '.search-results'
    ];

    for (const selector of sectionSelectors) {
      const section = document.querySelector(selector);
      if (section) {
        // Try to extract from existing stock indicators
        const inStockElement = section.querySelector(`.${CONFIG.inStockClass}`);
        const outOfStockElement = section.querySelector(`.${CONFIG.outOfStockClass}`);

        if (inStockElement && inStockElement.textContent.trim()) {
          return {
            inStock: inStockElement.textContent.trim(),
            outOfStock: outOfStockElement ? outOfStockElement.textContent.trim() : 'Tijdelijk uitverkocht'
          };
        }
      }
    }

    // Fallback to defaults
    return {
      inStock: 'Direct leverbaar',
      outOfStock: 'Tijdelijk uitverkocht'
    };
  }

  /**
   * Initialize stock sync
   */
  function init() {
    // Find all product cards on the page
    const productCards = document.querySelectorAll(CONFIG.productCardSelector);

    if (productCards.length === 0) {
      return; // No product cards on this page
    }

    themeLog(`Product Card Stock Sync: Found ${productCards.length} product cards`);

    // Get stock status text
    const stockText = getStockStatusText();

    // Process cards in batches
    processCardsInBatches(productCards, stockText.inStock, stockText.outOfStock)
      .then(() => {
        themeLog('Product Card Stock Sync: Complete');
      })
      .catch(error => {
        console.error('Product Card Stock Sync: Error', error);
      });
  }

  // Run after DOM is ready and after a small delay to let Shopify's JS finish
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setTimeout(init, CONFIG.debounceDelay);
    });
  } else {
    setTimeout(init, CONFIG.debounceDelay);
  }

  // Re-run when Shopify section is re-rendered (e.g., after filtering)
  document.addEventListener('shopify:section:load', () => {
    setTimeout(init, CONFIG.debounceDelay);
  });

})();
